import * as React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Switch from "@mui/material/Switch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControlLabel from "@mui/material/FormControlLabel";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector, useDispatch } from "react-redux";
import {
  round,
  has,
  map,
  isEmpty,
  merge,
  keyBy,
  union,
  cloneDeep,
  difference,
  flatMap,
  omit,
  isNull,
} from "lodash";
import { usePromiseTracker } from "react-promise-tracker";
import dayjs from "dayjs";
import configData from "../../../config.js";
import AccordionTitle from "../AccordionTitle";
import SelectionsCount from "../SelectionsCount";
import TableSummary from "../TableSummary";
import DiscountTable from "../DiscountTable";
import CommonDiscount from "../CommonDiscount";
import { withSnackbar } from "../../../components/SnackbarHOC";
import CommonDialog from "../../../components/CommonDialog";
import ExportButton from "../../../components/ExportButton";
import { handleCommonError, ENTITY_TYPES } from "../../../utils";
import useRequest from "../../../hooks/useRequest";
import useColumns from "../../../hooks/useColumns";
import usePermissions from "../../../hooks/usePermissions";
import PermissionsGate from "../../../permissions/PermissionsGate";
import { SCOPES } from "../../../permissions/permissions-map";
import { setGlobalLoader, setReportHeaders } from "../../../store/globalSlice";

const label = { inputProps: { "aria-label": "controlled" } };

const getPriorityDiscount =
  (commonDiscount, _priorityDiscounts) =>
  (individualDiscount, type, _id = "") => {
    const currentValue = !!commonDiscount[type]
      ? !isNull(commonDiscount[type])
        ? round(commonDiscount[type], 2)
        : null
      : -1;
    if (currentValue >= 0) {
      return currentValue;
    } else {
      return !isNull(individualDiscount) ? round(individualDiscount, 2) : null;
    }
  };

const getPriorityDiscountMultiplier =
  (commonMultiplier, editedItems) =>
  (individualMultiplier, id = "") => {
    if (Object.keys(editedItems).includes(id)) {
      if (editedItems[id].includes("multiplier")) {
        return individualMultiplier;
      }
    }
    return !!commonMultiplier ? commonMultiplier : individualMultiplier;
  };

const getPriorityMultiplier =
  (commonMultiplier) => (individualMultiplier, entity) => {
    if (Object.keys(commonMultiplier).includes(entity)) {
      return commonMultiplier[entity];
    } else {
      return individualMultiplier;
    }
  };

const UnsavedChangesDialogContent = React.memo(({ type }) => (
  <Typography variant="body1" component="h1">
    {`Changes you made will not be saved for this ${type}. Are you sure you
    wanted to proceed?`}
  </Typography>
));

function AccordionList({
  applyDisc,
  selectedOnly = false,
  filterData,
  Data,
  itemData,
  snackbarShowMessage,
  entityType = ENTITY_TYPES.discount,
  getItemDetails,
}) {
  const { promiseInProgress } = usePromiseTracker();
  const { requestMaker } = useRequest();
  const { hasPermission } = usePermissions();
  const dispatch = useDispatch();
  const commonDiscountRef = React.useRef();
  const [displayItemDetails, setDisplayItemDetails] = React.useState({});
  const [scopeChanges, setScopeChanges] = React.useState([]);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    React.useState(false);
  const [displayScope, setDisplayScope] = React.useState("");
  const [originalData, setOriginalData] = React.useState({ ...Data });
  const [baseData, setBaseData] = React.useState({ ...Data });
  const [priorityDiscounts, setPriorityDiscounts] = React.useState({});
  const [selectionRows, setSelectionRows] = React.useState({});
  const [selectedAcc, setSelectedAcc] = React.useState([]);
  const [commonMultiplier, setCommonMultiplier] = React.useState({});
  const [dataHolder, setDataHolder] = React.useState({ ...Data });
  const [currentDetails, setCurrentDetails] = React.useState({ ...Data });
  const [serverDefaultItemSelections, setServerDefaultItemSelections] =
    React.useState({});
  const store = useSelector((store) => store.global.store);
  const user = useSelector((store) => store.global.user);
  const { updatedColumns } = useColumns({
    entityType,
    isDisplayItem: !isEmpty(displayItemDetails),
  });

  React.useEffect(() => {
    dispatch(setReportHeaders(updatedColumns));
  }, [updatedColumns, dispatch]);

  const selectionDifference = React.useMemo(() => {
    return Object.keys(serverDefaultItemSelections).reduce((acc, scope) => {
      const currentDifference = difference(
        serverDefaultItemSelections[scope],
        selectionRows[scope]
      );
      return {
        ...acc,
        [scope]: currentDifference,
      };
    }, {});
  }, [selectionRows, serverDefaultItemSelections]);

  // Used while saving updated data
  const combinedItemSelections = React.useMemo(() => {
    return Object.keys({
      ...serverDefaultItemSelections,
      ...selectionRows,
    }).reduce((acc, scope) => {
      return {
        ...acc,
        [scope]: union(
          selectionRows[scope],
          serverDefaultItemSelections[scope]
        ),
      };
    }, {});
  }, [selectionRows, serverDefaultItemSelections]);

  const handleHeaderSelectAll = React.useCallback(
    (e) => {
      setSelectionRows((prevSelectionRows) => {
        const scope = e.target.value;
        let currentSelectedRows = { ...prevSelectionRows };
        if (!e.target.checked) {
          delete currentSelectedRows[scope];
        } else {
          const selections = currentDetails[scope].map((row) => row.id);
          currentSelectedRows = {
            ...currentSelectedRows,
            [scope]: [...selections],
          };
        }
        return currentSelectedRows;
      });
    },
    [currentDetails]
  );

  const getSelections = React.useCallback(
    (useOriginal = false, baseData = {}) => {
      let updatedData = !useOriginal ? dataHolder : currentDetails;
      //Note: This is be used to get original data while resetting all details.
      if (!isEmpty(baseData)) {
        return baseData;
      }
      if (!selectedOnly) {
        return updatedData;
      }
      const flatSelections = Object.keys(selectionRows).reduce(
        (acc, val) => (acc = [...acc, ...selectionRows[val]]),
        []
      );
      return Object.keys(updatedData).reduce((acc, data) => {
        const selections = updatedData[data].filter((row) => {
          if (flatSelections.length && flatSelections.includes(row.id)) {
            return row;
          }
        });
        return {
          ...acc,
          ...(selections.length
            ? {
                [data]: selections,
              }
            : {}),
        };
      }, {});
    },
    [currentDetails, dataHolder, selectedOnly, selectionRows]
  );

  const autoSetDisplayItemToggle = React.useCallback((filteredData) => {
    const updatedItemDisplayToggles = Object.keys(filteredData).reduce(
      (acc, scope) => {
        const itemLevelDataFound =
          filteredData[scope]?.find(
            (item) => has(item, "Item_Nbr") && !!item?.Item_Nbr
          ) ?? null;
        if (!!itemLevelDataFound) {
          return {
            ...acc,
            [scope]: true,
          };
        }
        return {
          ...acc,
        };
      },
      {}
    );
    setDisplayItemDetails(updatedItemDisplayToggles);
  }, []);

  const getServerPreSelections = React.useCallback((details) => {
    return Object.keys(details).reduce((acc, scope) => {
      const serverSelections = details[scope]
        .map((item) => {
          if (Boolean(item.Selected)) {
            return item.id;
          }
          return undefined;
        })
        ?.filter((val) => !!val);
      return {
        ...acc,
        [scope]: [...serverSelections],
      };
    }, {});
  }, []);

  React.useEffect(() => {
    if (!isEmpty(Data)) {
      // Resetting item toggle auto selection before current data gets applied.
      setDisplayItemDetails({});
      // Resetting server pre selection before current data gets applied.
      setServerDefaultItemSelections({});

      const serverItemSelections = getServerPreSelections(Data);
      // Adding to selection rows for default item selection.
      setSelectionRows(serverItemSelections);
      // Storing default selections selections for later use
      // since selections rows changes over time.
      setServerDefaultItemSelections(serverItemSelections);
      autoSetDisplayItemToggle({ ...Data });
      setCurrentDetails({ ...Data });
      setOriginalData({ ...Data });
      setBaseData({ ...Data });
    }
  }, [Data, autoSetDisplayItemToggle, getServerPreSelections]);

  // Note: Used to handle item data handling ONLY
  // hance merging should happen in order to retain previous data.
  React.useEffect(() => {
    if (!isEmpty(itemData)) {
      const onlyCurrentScopeData = {
        [displayScope]: [...currentDetails[displayScope]],
      };
      // Adding to selection rows for pre server item selection.
      const serverItemSelections = getServerPreSelections({
        ...onlyCurrentScopeData,
        ...itemData,
      });
      // Skips current selection scope reset selections will get merged
      const mergedSelections = {
        ...serverItemSelections,
        ...omit(selectionRows, displayScope),
      };
      setServerDefaultItemSelections({ ...mergedSelections });
      setBaseData({ ...baseData, ...itemData });
      setCurrentDetails({ ...currentDetails, ...itemData });
      setSelectionRows({
        ...mergedSelections,
      });
      autoSetDisplayItemToggle({
        ...currentDetails,
        ...itemData,
      });
      setOriginalData({ ...originalData, ...itemData });
      setDataHolder({ ...dataHolder, ...itemData });
    }
    /* eslint-disable-next-line */
  }, [itemData]);

  React.useEffect(() => {
    const baseData = getSelections(true);
    setDataHolder({ ...baseData });
    setCurrentDetails({ ...baseData });
  }, []);

  React.useEffect(() => {
    if (selectedOnly) {
      setCurrentDetails({ ...getSelections(selectedOnly) });
    }
  }, [selectionRows, selectedOnly]);

  React.useEffect(() => {
    const updatedHeaderSelections = Object.keys(selectionRows).reduce(
      (acc, scope) => {
        if (
          has(currentDetails, scope) &&
          selectionRows[scope].length === currentDetails[scope].length
        ) {
          return [...acc, scope];
        }
        return [...acc];
      },
      []
    );
    setSelectedAcc([...updatedHeaderSelections]);
  }, [selectionRows, currentDetails]);

  const getMergedData = React.useCallback(
    (details) => {
      return Object.keys(dataHolder).reduce((acc, item) => {
        if (details[item]) {
          const initialDetails = cloneDeep(dataHolder[item]);
          const merges =
            merge(keyBy(initialDetails, "id"), keyBy(details[item], "id")) ??
            {};
          // Maintains correct sequence of fetched data.
          const updatedDataHolder = Object.keys(dataHolder).reduce(
            (acc, scope) => {
              const mappedData = dataHolder[scope]?.map(
                (row) => merges[row.id]
              );
              return {
                ...acc,
                [scope]: [...mappedData],
              };
            },
            {}
          );
          return {
            ...acc,
            [item]: [...updatedDataHolder[item]],
          };
        }
        return {
          ...acc,
          [item]: dataHolder[item],
        };
      }, {});
    },
    [dataHolder]
  );

  React.useEffect(() => {
    // Merge selected & updated changes with original(i.e. Previously Stored)
    if (!selectedOnly) {
      const mergedDetails = getMergedData(currentDetails);
      setDataHolder({ ...mergedDetails });
      setCurrentDetails({ ...mergedDetails });
    } else {
      // Before updating save changes
      setDataHolder({ ...currentDetails });
      setCurrentDetails({ ...getSelections(selectedOnly) });
    }
  }, [selectedOnly]);

  const handleSave = React.useCallback((commonDetails) => {
    dispatch(setGlobalLoader(true));
    if (entityType === ENTITY_TYPES.discount) {
      const getDiscount = getPriorityDiscount(commonDetails, priorityDiscounts);
      const getMultiplier = getPriorityDiscountMultiplier(
        commonDetails.Multiplier,
        priorityDiscounts
      );

      // Use combined selections(server selected defaults + new selections)
      // here with selection difference to define payload.
      const updatedDetails = Object.keys(combinedItemSelections).reduce(
        (acc, data) => {
          const currentScopeSelectionDifference = has(
            selectionDifference,
            data
          );
          const selections = currentDetails[data]
            ?.map((row) => {
              if (combinedItemSelections[data].includes(row.id)) {
                let originalRowData = { ...row };
                let isDeSelected =
                  currentScopeSelectionDifference &&
                  selectionDifference[data]?.includes(row.id);

                // used to reset to original/previous values if changed.
                if (isDeSelected) {
                  originalRowData = originalData[data].find((item) => item.id);
                }
                return {
                  id: row?.id,
                  storeId: store.id,
                  storeItemId: row?.Item_Nbr?.length ? row?.Item_Nbr : null,
                  storePlanogramId: row?.PlanogramNumber,
                  coreDiscountPercent: isDeSelected
                    ? originalRowData?.CoreDiscountPercent
                    : getDiscount(
                        row?.CoreDiscountPercent,
                        "CoreDiscountPercent",
                        row?.id
                      ),
                  nonCoreDiscountPercent: isDeSelected
                    ? originalRowData?.NonCoreDiscountPercent
                    : getDiscount(
                        row?.NonCoreDiscountPercent,
                        "NonCoreDiscountPercent",
                        row?.id
                      ),
                  datingDays: isDeSelected
                    ? originalRowData?.DatingDays
                    : getDiscount(row?.DatingDays, "DatingDays", row?.id),
                  discMultiplier: isDeSelected
                    ? originalRowData?.discMultiplier
                    : getMultiplier(row?.discMultiplier, row?.id),
                  userId: user.email,
                  recUniqueID: row?.recUniqueID,
                  selected: isDeSelected ? 0 : 1,
                };
              }
              return undefined;
            })
            .filter((item) => !!item);
          return [...acc, ...(selections.length ? selections : [])];
        },
        []
      );

      requestMaker({
        method: "post",
        url: configData.SaveDiscount,
        data: updatedDetails,
      })
        .then((res) => {
          const currentRowSelections = { ...selectionRows };
          applyDiscount(updatedDetails);
          resetDiscount();
          setSelectionRows(currentRowSelections);
          // Combined selection will become server default selection.
          setServerDefaultItemSelections(currentRowSelections);
          snackbarShowMessage("Discount applied successfully");
        })
        .catch(() => {
          handleCommonError(snackbarShowMessage);
        })
        .finally(() => {
          dispatch(setGlobalLoader(false));
        });
    } else if (entityType === ENTITY_TYPES.planogram) {
      const getMultiplier = getPriorityMultiplier(commonMultiplier);

      const updatedDetails = Object.keys(combinedItemSelections).reduce(
        (acc, data) => {
          const currentScopeSelectionDifference = has(
            selectionDifference,
            data
          );
          const selections = currentDetails[data]
            ?.map((row) => {
              if (combinedItemSelections[data].includes(row.id)) {
                let originalRowData = { ...row };
                let isDeSelected =
                  currentScopeSelectionDifference &&
                  selectionDifference[data]?.includes(row.id);

                // used to reset to original/previous values if changed.
                if (isDeSelected) {
                  originalRowData = originalData[data].find((item) => item.id);
                }

                const currentMultiplier = getMultiplier(row.multiplier, data)
                  ? getMultiplier(row.multiplier, data)
                  : null;

                return {
                  id: row?.id,
                  storeId: store.id,
                  storeItemId: row?.Item_Nbr?.length ? row?.Item_Nbr : null,
                  storePlanogramId: row?.PlanogramNumber,
                  multiplier: isDeSelected
                    ? originalRowData.multiplier
                    : currentMultiplier,
                  plannerNotes: isDeSelected
                    ? originalRowData.plannernotesid
                    : row.plannernotesid,
                  plannerAction: isDeSelected
                    ? originalRowData.PlannerAction
                    : Boolean(row.PlannerAction),
                  retailerNotes: isDeSelected
                    ? originalRowData.retailernotesid
                    : row.retailernotesid,
                  retailerAction: isDeSelected
                    ? originalRowData.RetailerAction
                    : Boolean(row.RetailerAction),
                  userId: user.email,
                  recUniqueID: row?.recUniqueID,
                  selected: isDeSelected ? 0 : 1,
                };
              }
              return undefined;
            })
            .filter((item) => !!item);
          return [...acc, ...(selections.length ? selections : [])];
        },
        []
      );

      requestMaker({
        method: "post",
        url: configData.SavePlanogram,
        data: updatedDetails,
      })
        .then((res) => {
          const currentRowSelections = { ...selectionRows };
          //TODO: need to check apply multiplier argument.
          applyMultiplier(updatedDetails);
          setCommonMultiplier({});
          setSelectionRows({});
          setSelectionRows(currentRowSelections);
          // Combined selection will become server default selection.
          setServerDefaultItemSelections(currentRowSelections);
          snackbarShowMessage("Planogram data is updated successfully");
        })
        .catch(() => {
          handleCommonError(snackbarShowMessage);
        })
        .finally(() => {
          dispatch(setGlobalLoader(false));
        });
    } else {
      const date = commonDetails;
      const updatedDetails = Object.keys(selectionRows).reduce((acc, data) => {
        const selections = currentDetails[data]
          ?.map((row) => {
            if (selectionRows[data].includes(row.id)) {
              return {
                ...row,
                releaseDateTime: dayjs(date).format(),
              };
            }
          })
          .filter((item) => !!item);
        return [...acc, ...(selections.length ? selections : [])];
      }, []);
      //TODO: Call API
      console.log(date);
      console.log(updatedDetails);
      applyReleaseDateTime(updatedDetails);
      //TODO: On Success - update current details / take updates from server
    }
  });

  const applyReleaseDateTime = React.useCallback(
    (updatedDetails, applyType = "save") => {
      const updatedCurrentDetails = Object.keys(originalData).reduce(
        (acc, key) => {
          const rows = originalData[key];
          const updatedRows = rows.map((row) => {
            if (selectionRows[key]?.includes(row.id)) {
              const updatedRow = updatedDetails.find(
                (item) => item.id === row.id
              );

              return {
                ...row,
                release_date_time:
                  applyType === "delete" ? "" : updatedRow?.releaseDateTime,
              };
            }
            return row;
          });

          return {
            ...acc,
            [key]: updatedRows,
          };
        },
        {}
      );
      setSelectionRows({});
      setCurrentDetails({ ...updatedCurrentDetails });
      const updatedMergedDetails = getMergedData(updatedCurrentDetails);
      setOriginalData({ ...updatedMergedDetails });
      setDataHolder({ ...updatedMergedDetails });
      setBaseData({ ...updatedMergedDetails });
      setDisplayScope("");
      setScopeChanges([]);
      snackbarShowMessage("Release dates saved successfully");
    },
    [currentDetails, selectionRows, getMergedData, snackbarShowMessage]
  );

  const applyDiscount = (updatedDetails) => {
    const updatedCurrentDetails = Object.keys(originalData).reduce(
      (acc, key) => {
        const rows = originalData[key] ?? [];
        const discountedRows = rows?.map((row) => {
          if (selectionRows[key]?.includes(row.id)) {
            const discountedRow = updatedDetails?.find(
              (discountRow) => String(discountRow.id) === String(row.id)
            );
            return {
              ...row,
              CoreDiscountPercent: discountedRow?.coreDiscountPercent,
              DatingDays: discountedRow?.datingDays,
              NonCoreDiscountPercent: discountedRow?.nonCoreDiscountPercent,
              discMultiplier: discountedRow?.discMultiplier,
            };
          }
          return row;
        });

        return {
          ...acc,
          [key]: discountedRows,
        };
      },
      {}
    );
    setCurrentDetails({ ...updatedCurrentDetails });
    const updatedMergedDetails = getMergedData(updatedCurrentDetails);
    setOriginalData({ ...updatedMergedDetails });
    setDataHolder({ ...updatedMergedDetails });
    setBaseData({ ...updatedMergedDetails });
    setDisplayScope("");
    setScopeChanges([]);
  };

  const applyMultiplier = (updatedDetails) => {
    const updatedCurrentDetails = Object.keys(originalData).reduce(
      (acc, key) => {
        const rows = originalData[key];
        const updatedRows = rows.map((row) => {
          if (selectionRows[key]?.includes(row.id)) {
            const updatedRow = updatedDetails.find(
              (item) => String(item?.id) === String(row.id)
            );
            return {
              ...row,
              multiplier: updatedRow?.multiplier,
              plannernotesid: updatedRow?.plannerNotes,
              retailernotesid: updatedRow?.retailerNotes,
              PlannerAction: Number(updatedRow?.plannerAction),
              RetailerAction: Number(updatedRow?.retailerAction),
            };
          }
          return row;
        });

        return {
          ...acc,
          [key]: updatedRows,
        };
      },
      {}
    );
    setCurrentDetails({ ...updatedCurrentDetails });
    const updatedMergedDetails = getMergedData(updatedCurrentDetails);
    setOriginalData({ ...updatedMergedDetails });
    setDataHolder({ ...updatedMergedDetails });
    setBaseData({ ...updatedMergedDetails });
    setDisplayScope("");
    setScopeChanges([]);
  };

  const handleProcessedRow = React.useCallback(
    (key) => (updatedRow, fieldType) => {
      const processedRow = currentDetails[key].map((row) => {
        if (updatedRow.id === row.id) {
          setPriorityDiscounts((prevState) => ({
            ...prevState,
            [row.id]: prevState[row.id]
              ? prevState[row.id].includes(fieldType)
                ? [...prevState[row.id]]
                : [...prevState[row.id], fieldType]
              : [fieldType],
          }));
          return {
            ...row,
            ...updatedRow,
          };
        }
        return row;
      });
      setCurrentDetails((details) => ({
        ...details,
        [key]: [...processedRow],
      }));
      setScopeChanges((prevScopes) => union(prevScopes, [key]));
    },
    [currentDetails]
  );

  const resetDiscount = () => {
    if (commonDiscountRef.current) {
      commonDiscountRef.current.resetDiscount();
    }
    setSelectionRows({});
    setPriorityDiscounts({});
    setSelectedAcc([]);
    setDisplayScope("");
    setScopeChanges([]);
  };

  const handleReset = React.useCallback(() => {
    const details = getSelections(false, baseData);
    resetDiscount();
    setCommonMultiplier({});
    setCurrentDetails({ ...details });
    setOriginalData({ ...details });
    setDataHolder({ ...details });
    // Resetting server default selection back with original values.
    setSelectionRows({ ...serverDefaultItemSelections });
  }, [getSelections, baseData, serverDefaultItemSelections]);

  const handleCreatePONumbers = React.useCallback(() => {
    //TODO: create PO Numbers logic goes here.
    snackbarShowMessage("Saved changes to PO number type");

    //TODO: send selections to API for creating PO Numbers
    console.log(selectionRows);

    setSelectionRows({});
  }, [selectionRows, snackbarShowMessage]);

  const handleDeleteDates = React.useCallback(() => {
    const updatedDetails = Object.keys(selectionRows).reduce((acc, data) => {
      const selections = currentDetails[data]
        ?.map((row) => {
          if (selectionRows[data].includes(row.id)) {
            return {
              ...row,
              releaseDateTime: "",
            };
          }
        })
        .filter((item) => !!item);
      return [...acc, ...(selections.length ? selections : [])];
    }, []);
    //TODO: Call API
    applyReleaseDateTime(updatedDetails, "delete");
    //TODO: Delete dates logic goes here.
    snackbarShowMessage("Release dates deleted successfully");
  }, [
    snackbarShowMessage,
    applyReleaseDateTime,
    currentDetails,
    selectionRows,
  ]);

  const handleGlobalSelectAll = React.useCallback(
    (e) => {
      if (e.target.checked) {
        const entities = Object.keys(currentDetails);
        setSelectedAcc(entities);
        const selectedDetails = entities.reduce((accumulator, entity) => {
          return {
            ...accumulator,
            [entity]: map(currentDetails[entity], "id"),
          };
        }, {});
        setSelectionRows(selectedDetails);
      } else {
        setSelectedAcc([]);
        setSelectionRows({});
      }
    },
    [currentDetails]
  );

  const getGlobalCheck = React.useMemo(() => {
    const entityList = Object.keys(currentDetails);
    let checkIndividuals = true;

    if (Object.keys(selectionRows).length) {
      checkIndividuals = entityList.reduce((acc, entity) => {
        if (currentDetails[entity].length !== selectionRows[entity]?.length) {
          acc = true;
        }
        return acc;
      }, false);
    }

    return (
      (selectedAcc.length &&
        entityList?.length === selectedAcc?.length &&
        !checkIndividuals) ||
      !checkIndividuals ||
      selectedOnly
    );
  }, [selectionRows, selectedAcc, currentDetails, selectedOnly]);

  const handleCommonMultiplierChange = React.useCallback((newValue, entity) => {
    if (!newValue) {
      return setCommonMultiplier((prevState) => {
        const updatedState = { ...prevState };
        delete updatedState[entity];
        return {
          ...updatedState,
        };
      });
    }
    return setCommonMultiplier((prevState) => ({
      ...prevState,
      [entity]: newValue,
    }));
  }, []);

  const currentDetailsHasData = React.useMemo(() => {
    return !isEmpty(currentDetails);
  }, [currentDetails]);

  const revertSelectionOnError = React.useCallback((scope) => {
    setDisplayScope("");
    setDisplayItemDetails((prevState) => ({
      ...prevState,
      [scope]: false,
    }));
  }, []);

  const setItemDetailsOnSuccess = React.useCallback(
    (currentToggle, currentScope) => () => {
      setDisplayScope(currentScope);
      setScopeChanges((prevScopes) =>
        prevScopes.filter((s) => s !== currentScope)
      );
      setDisplayItemDetails((prevState) => ({
        ...prevState,
        [currentScope]: currentToggle,
      }));
    },
    []
  );

  const updateScopeDetails = React.useCallback(
    (scope = "") => {
      const currentScope = !!scope ? scope : displayScope;
      const currentToggle = has(displayItemDetails, currentScope)
        ? !displayItemDetails[currentScope]
        : true;
      const updateItemData = setItemDetailsOnSuccess(
        currentToggle,
        currentScope
      );
      getItemDetails(
        currentScope,
        currentToggle,
        revertSelectionOnError,
        updateItemData
      );
    },
    [
      displayItemDetails,
      displayScope,
      getItemDetails,
      revertSelectionOnError,
      setItemDetailsOnSuccess,
    ]
  );

  const handleGetItemDetails = React.useCallback(
    (scope) => {
      if (scopeChanges?.includes(scope)) {
        setShowUnsavedChangesDialog(true);
        setDisplayScope(scope);
      } else {
        updateScopeDetails(scope);
      }
    },
    [scopeChanges, updateScopeDetails]
  );

  const handleUnsavedChangesClose = React.useCallback(() => {
    setShowUnsavedChangesDialog(false);
  }, []);

  const handleUnsavedChangesDone = React.useCallback(() => {
    updateScopeDetails();
    handleUnsavedChangesClose();
  }, [handleUnsavedChangesClose, updateScopeDetails]);

  const disableAction = React.useMemo(
    () =>
      store?.pending ||
      store?.isArchived ||
      !hasPermission({ scopes: [SCOPES.canEdit] }),
    [store, hasPermission]
  );

  return (
    <>
      <Paper sx={{ padding: 1, marginTop: 2 }}>
        <PermissionsGate scopes={[SCOPES.canEdit]}>
          {currentDetailsHasData && (
            <Grid
              container
              item
              xs={12}
              sm={12}
              md={12}
              lg={12}
              justifyContent="space-between"
            >
              <FormControlLabel
                sx={{ my: 1, mx: 0 }}
                control={
                  <Switch
                    size="small"
                    sx={{ ml: 1 }}
                    checked={getGlobalCheck}
                    onChange={handleGlobalSelectAll}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{ color: "text.secondary", fontWeight: "bold" }}
                  >
                    Select All
                  </Typography>
                }
                labelPlacement="start"
              />
              <ExportButton
                body={flatMap(currentDetails)}
                isExportAll
                selections={selectionRows}
              />
            </Grid>
          )}
        </PermissionsGate>
        {currentDetailsHasData &&
          [ENTITY_TYPES.planogram, ENTITY_TYPES.orders].includes(
            entityType
          ) && (
            <TableSummary
              entityDetails={currentDetails}
              entityType={entityType}
              itemLevel={displayItemDetails}
            />
          )}
        {Object.keys(currentDetails).map((key) => {
          return (
            <Accordion
              key={key}
              TransitionProps={{ unmountOnExit: true }}
              sx={{ my: 1, borderRadius: 1 }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                sx={{
                  backgroundColor: "#F1F1F1",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Switch
                    {...label}
                    size="small"
                    sx={{ mr: 2 }}
                    checked={
                      currentDetails[key]?.length === selectionRows[key]?.length
                    }
                    value={key}
                    onChange={handleHeaderSelectAll}
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  />
                  <AccordionTitle
                    type={applyDisc}
                    currentDetails={currentDetails[key][0] ?? {}}
                    headerDetails={filterData}
                  />
                  <SelectionsCount
                    type={applyDisc}
                    multiplierEntity={key}
                    entityType={entityType}
                    selectionsArray={selectionRows[key]}
                    totalCount={currentDetails[key].length}
                    handleCommonMultiplierChange={handleCommonMultiplierChange}
                    multiplierValue={commonMultiplier[key]}
                    getItemDetails={handleGetItemDetails}
                    itemDisplay={displayItemDetails[key] ?? false}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <DiscountTable
                  row={currentDetails[key]}
                  displayItem={displayItemDetails[key] ?? false}
                  preSelections={
                    has(selectionRows, key) ? selectionRows[key] : []
                  }
                  getProcessedRow={handleProcessedRow(key)}
                  getSelectedRows={(ids) => {
                    setSelectionRows((prevSelectionRows) => {
                      let currentDetails = { ...prevSelectionRows };
                      if (!ids.length) {
                        delete currentDetails[key];
                      } else {
                        currentDetails = {
                          ...currentDetails,
                          [key]: [...ids],
                        };
                      }
                      return currentDetails;
                    });
                  }}
                  entityType={entityType}
                />
              </AccordionDetails>
            </Accordion>
          );
        })}
        <CommonDiscount
          ref={commonDiscountRef}
          entityType={entityType}
          handleCommonDiscountSave={handleSave}
          handleCommonDiscountReset={handleReset}
          handleCreatePONumbers={handleCreatePONumbers}
          handleDeleteDates={handleDeleteDates}
          disabled={!currentDetailsHasData || disableAction}
          disableSave={!!flatMap(combinedItemSelections).length}
        />
      </Paper>
      {promiseInProgress && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <CommonDialog
        open={showUnsavedChangesDialog}
        title="Unsaved Changes"
        content={<UnsavedChangesDialogContent type={applyDisc} />}
        type="yesNo"
        handleClose={handleUnsavedChangesClose}
        handleDone={handleUnsavedChangesDone}
      />
    </>
  );
}

export default withSnackbar(AccordionList);
